/** Run a failed (or stopped) release / restore job again (tenant retirement CONTRACT §8.7,
 *  `POST …/requests/{id}/retry/`): a new job resumes at the step stored on the request — every step
 *  is safe to run again. A confirmation, no reason (the retry is audited). While the job still runs
 *  the backend refuses (409 `not_retryable`), which turns Retry off and re-reads the tab. */
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';

import { lifecycleApi, lifecycleError, lifecycleErrorWords } from '@/api/tenantLifecycle';
import type { TenantRequest } from '@/api/tenantRequests';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { jobOf, stepLabel } from './lifecycleJob';
import { useLifecycleRefresh } from './useLifecycleRefresh';

interface Props {
  topId: number;
  /** The job's request; null keeps the dialog closed. */
  request: TenantRequest | null;
  onClose: () => void;
}

export function RetryLifecycleDialog({ topId, request, onClose }: Props) {
  const snackbar = useSnackbar();
  const refresh = useLifecycleRefresh();
  const mut = useMutation({
    mutationFn: (id: number) => lifecycleApi.retry(topId, id),
    onSuccess: () => {
      snackbar.show('The job is running again.', 'success');
      refresh();
      onClose();
    },
    onError: (e) => {
      if (lifecycleError(e)?.code === 'not_retryable') refresh();
    },
  });

  const { reset: resetMutation } = mut;
  useEffect(() => {
    if (request) resetMutation();
  }, [request, resetMutation]);

  const refused = lifecycleError(mut.error)?.code === 'not_retryable';
  const job = request ? jobOf(request) : null;
  const what = job?.kind === 'restore' ? 'restore' : 'release';
  const from = job?.step && job.step !== 'done' ? stepLabel(job, job.step) : null;

  return (
    <AppDialog
      open={request != null}
      onClose={onClose}
      title="Run the job again"
      actions={
        <>
          <Button onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={mut.isPending || refused}
            onClick={() => request && mut.mutate(request.id)}
            startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Run again
          </Button>
        </>
      }
    >
      {request && (
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Typography variant="body2">
            A new job runs the {what} again from the step it stopped at
            {from ? <> — <strong>{from}</strong></> : ''}. Every step is safe to run again; fix what
            made it fail first.
          </Typography>
          {request.status !== 'failed' && (
            <Alert severity="warning">
              Only when the job has stopped — its worker died and the Background tasks page shows no
              live job. While it still runs, the retry is refused.
            </Alert>
          )}
          {mut.error != null && <Alert severity="error">{lifecycleErrorWords(mut.error)}</Alert>}
        </Box>
      )}
    </AppDialog>
  );
}
